/**
 * CheckpointStatusTag Components
 * Tags for displaying checkpoint status with checkpoint-specific labels
 *
 * Uses the same status keys as TaskStatusTag (completed, overdue, urgent, skipped)
 * but with checkpoint-specific labels (Good to Go!, Needs a Nudge, etc.)
 *
 * Variants:
 * - IconBadge: Small icon with tooltip (for inline use)
 * - Badge: Pill with icon and text label (e.g., "Good to Go!")
 */

import { Box, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import { LuCircleCheck, LuClock, LuFlame, LuCircleSlash, LuActivity, LuSend } from "react-icons/lu";
import { BadgeSize, BADGE_SIZE_STYLES } from "design-system/foundation/BadgeStyles";

const STATUS_CONFIG = {
  completed: {
    icon: LuCircleCheck,
    label: "Good to Go!",
    bg: "green.50",
    color: "green.500",
  },
  overdue: {
    icon: LuClock,
    label: "Needs a Nudge",
    bg: "orange.50",
    color: "orange.500",
  },
  urgent: {
    icon: LuFlame,
    label: "Behind Schedule",
    bg: "red.50",
    color: "red.500",
  },
  skipped: {
    icon: LuCircleSlash,
    label: "Skipped",
    bg: "gray.100",
    color: "gray.500",
  },
  evaluated: {
    icon: LuActivity,
    label: "Evaluated",
    bg: "blue.50",
    color: "blue.500",
  },
  sent: {
    icon: LuSend,
    label: "Sent",
    bg: "green.50",
    color: "green.500",
  },
} as const;

export type CheckpointStatusType = keyof typeof STATUS_CONFIG;

interface IconBadgeProps {
  status: CheckpointStatusType;
  size?: BadgeSize;
}

function IconBadge({ status, size = "md" }: IconBadgeProps) {
  const config = STATUS_CONFIG[status];
  if (!config) return null;

  const styles = BADGE_SIZE_STYLES[size];

  return (
    <Tooltip label={config.label} placement="top" hasArrow>
      <Box as="span" display="inline-flex">
        <Icon
          as={config.icon}
          color={config.color}
          boxSize={styles.iconSize}
          flexShrink={0}
          title={config.label}
          cursor="default"
        />
      </Box>
    </Tooltip>
  );
}

interface BadgeProps {
  status: CheckpointStatusType;
  size?: BadgeSize;
}

function Badge({ status, size = "md" }: BadgeProps) {
  const config = STATUS_CONFIG[status];
  if (!config) return null;

  const styles = BADGE_SIZE_STYLES[size];

  return (
    <HStack
      spacing={1}
      bg={config.bg}
      color={config.color}
      px={styles.px}
      py={styles.py}
      borderRadius="md"
      display="inline-flex"
    >
      <Icon as={config.icon} boxSize={styles.iconSize} />
      <Text fontSize={styles.fontSize} fontWeight="medium">
        {config.label}
      </Text>
    </HStack>
  );
}

export const CheckpointStatusTag = {
  IconBadge,
  Badge,
};
